import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CalendarDays, Plus, MapPin, Tag, Sparkles } from "lucide-react";
import { toast, Toaster } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";

export default function Events() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cat, setCat] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [credits, setCredits] = useState(0);
  const [form, setForm] = useState({
    title: "", category: "Dégustation", description: "", start_date: "",
    end_date: "", location: "", image_url: "", price: "", capacity: "", website: "",
  });

  const load = () => {
    api.get("/events", { params: cat ? { category: cat } : {} })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  };

  useEffect(() => { load(); }, [cat]); // eslint-disable-line
  useEffect(() => {
    api.get("/events-meta/categories")
      .then((r) => setCategories(Array.isArray(r.data?.categories) ? r.data.categories : []))
      .catch(() => setCategories([]));
    if (user) api.get("/me/billing").then((r) => setCredits(r.data.credits)).catch(() => {});
  }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        price: form.price ? parseFloat(form.price) : null,
        capacity: form.capacity ? parseInt(form.capacity) : null,
      };
      const { data } = await api.post("/events", payload);
      toast.success(`Événement publié. Crédits restants : ${data.credits_left}`);
      setCredits(data.credits_left);
      setShowForm(false);
      setForm({ title: "", category: "Dégustation", description: "", start_date: "", end_date: "", location: "", image_url: "", price: "", capacity: "", website: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-12 md:py-16">
      <Toaster theme="light" />
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <span className="chip-pink">Annuaire</span>
          <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">Events</h1>
          <p className="text-[#4A5568] mt-3 max-w-2xl">Dégustations, visites, salons, vendanges — l'agenda œnotouristique.</p>
        </div>
        {user ? (
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FBF7F3] border border-[#1F3A5F]/10 text-xs text-[#1F3A5F]"><Sparkles className="w-3.5 h-3.5 text-[#E5276A]" /> {credits} crédit{credits !== 1 ? "s" : ""}</div>
            <button onClick={() => setShowForm((v) => !v)} data-testid="events-new" className="btn-primary"><Plus className="w-4 h-4" /> Publier un événement</button>
          </div>
        ) : (
          <Link to="/login?next=/events" className="btn-secondary">Connectez-vous pour publier</Link>
        )}
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        <button onClick={() => setCat("")} className={`px-3 py-1.5 rounded-full text-xs font-bold border ${cat === "" ? "bg-[#1F3A5F] text-white border-[#1F3A5F]" : "bg-white text-[#1F3A5F] border-[#1F3A5F]/15 hover:bg-[#FBF7F3]"}`}>Tous</button>
        {categories.map((c) => (
          <button key={c} onClick={() => setCat(c)} className={`px-3 py-1.5 rounded-full text-xs font-bold border ${cat === c ? "bg-[#1F3A5F] text-white border-[#1F3A5F]" : "bg-white text-[#1F3A5F] border-[#1F3A5F]/15 hover:bg-[#FBF7F3]"}`}>{c}</button>
        ))}
      </div>

      {showForm && (
        <form data-testid="events-form" onSubmit={submit} className="mt-8 grid md:grid-cols-2 gap-5 p-6 md:p-8 rounded-2xl border border-[#1F3A5F]/10 bg-white shadow-sm">
          <Field label="Titre *"><input required className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label="Catégorie *"><select required className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{categories.map((c) => <option key={c} value={c}>{c}</option>)}</select></Field>
          <Field label="Date début *"><input required type="datetime-local" className={inputCls} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
          <Field label="Date fin"><input type="datetime-local" className={inputCls} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
          <Field label="Lieu" full>
            <AddressAutocomplete
              value={form.location}
              onChange={(v) => setForm({ ...form, location: v })}
              onSelect={(place) => {
                setForm((f) => ({
                  ...f,
                  location: place.address || place.name || f.location,
                  lat: place.lat ?? f.lat,
                  lng: place.lng ?? f.lng,
                  google_place_id: place.place_id ?? f.google_place_id,
                }));
              }}
              placeholder="Adresse / ville / domaine"
              testId="events-location-ac"
            />
          </Field>
          <Field label="Description" full><textarea rows={3} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <Field label="Image (URL)"><input className={inputCls} value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} /></Field>
          <Field label="Lien externe"><input className={inputCls} value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></Field>
          <Field label="Prix €"><input type="number" step="0.5" className={inputCls} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></Field>
          <Field label="Capacité"><input type="number" className={inputCls} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></Field>
          <div className="md:col-span-2 flex items-center justify-between">
            <div className="text-xs text-[#8B95A1]">Coût : <strong className="text-[#E5276A]">1 crédit</strong> · Vous avez {credits} crédit{credits !== 1 ? "s" : ""}</div>
            <button data-testid="events-submit" disabled={credits <= 0} className="btn-primary disabled:opacity-50">Publier</button>
          </div>
        </form>
      )}

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.length === 0 && (
          <div className="col-span-full text-center py-16 text-[#8B95A1]"><CalendarDays className="w-8 h-8 mx-auto text-[#E5276A] mb-3" />Aucun événement publié.</div>
        )}
        {items.map((e) => (
          <div key={e.id} data-testid={`event-card-${e.id}`} className="app-card overflow-hidden">
            <div className="aspect-[4/3] overflow-hidden bg-[#FBF7F3]">
              {e.image_url ? <img src={e.image_url} alt={e.title} className="w-full h-full object-cover" /> : (
                <div className="w-full h-full flex items-center justify-center"><CalendarDays className="w-10 h-10 text-[#E5276A]/40" /></div>
              )}
            </div>
            <div className="p-5">
              <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-[#E5276A] font-bold"><Tag className="w-3 h-3" /> {e.category}</div>
              <div className="font-display text-xl text-[#1F3A5F] mt-1.5">{e.title}</div>
              <div className="text-sm text-[#8B95A1] mt-2 space-y-0.5">
                <div className="inline-flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-[#E5276A]" />{new Date(e.start_date).toLocaleString()}</div>
                {e.location && <div className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#E5276A]" />{e.location}</div>}
              </div>
              {e.price != null && <div className="mt-3 text-sm font-bold text-[#1F3A5F]">{e.price} €</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const inputCls = "w-full bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] focus:ring-2 focus:ring-[#E5276A]/10 outline-none rounded-lg px-4 py-2.5 text-sm text-[#1F3A5F] placeholder:text-[#8B95A1]";
const Field = ({ label, children, full }) => (
  <label className={`flex flex-col gap-1.5 ${full ? "md:col-span-2" : ""}`}>
    <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-bold">{label}</span>
    {children}
  </label>
);
